import { useAsync } from 'react-use';
import FunwooAPI, { SendEmailPersonalConsultationV2Dto, StaticOptionCategory, UTM } from '@/swagger';
import {
  Agreement,
  BasicFormValues,
  budgetOptions,
  createLectureOption,
  dDayOptions,
  EmailField,
  FunwooRadio,
  Input,
  interestingCityOptions,
  Label,
  NameField,
  PhoneField,
  PopupSelect,
  WhereDoYouKnowUsFrom,
} from './ConsultantForm';
import { stringToOption } from '@/src/helper/option';
import { trackLead } from '@/utils/facebookConversion';
import { FormProvider, useForm } from 'react-hook-form';
import StyleModule from '@/src/components/feature/ConsultantForm/consultant-form.module.scss';
import React, { useCallback } from 'react';
import classNames from 'classnames';
import { HookFormError } from '@/src/components/common/FieldError';
import { Button, useDisclosure } from '@chakra-ui/react';
import { GreetingPopup } from '@/src/components/feature/ConsultantForm/GreetingPopup';
import { useRouter } from 'next/router';
import clsx from 'clsx';
import { useReCaptcha } from '@/lib/next-recaptcha-v3';
import HintWording from '@/src/components/feature/ConsultantForm/HintWording';
import { multipleValueRequired } from '@/utils/fieldValidator';

export interface SendPersonalConsultationV2FormValues
  extends Omit<SendEmailPersonalConsultationV2Dto, 'type'>,
    BasicFormValues {
  phoneCountryCode: number;
}
export const industryOptions = [
  '製造業',
  '金融業',
  '資訊科技業',
  '醫療保健業',
  '服務業',
  '教育服務業',
  '專業技術與顧問服務',
  '傳播出版業',
  '自由業',
  '家管',
  '不動產業',
].map(stringToOption);

const jobLevelOptions = [
  '企業決策者（副總經理以上）',
  '特殊職位（顧問、特別助理）',
  '高階主管（經理級以上，總監以下）',
  '中階主管（副理級以上）',
  '初階主管',
  '一般職員',
  '其他',
].map(stringToOption);

export const availableTimeOptions = [
  '平日上午 10:00-12:00',
  '平日下午 14:00-16:00',
  '平日晚上 19:00-21:00',
  '皆可',
].map(stringToOption);

const getPersonalConsultantFormV2InitialValues = (): Partial<SendPersonalConsultationV2FormValues> => {
  return {
    // name: '',
    phoneCountryCode: 886,
    // phone: '',
    // email: '',

    // purpose: purposesForPersonalConsultation[0].value,
    // haveExperienced: 'true',
    // budget: budgetOptionsForPersonalConsultation[0].value,
    /**
     * System filled, not for user
     */
    budgetCurrency: 'USD',
    // dDay: dDayOptions[0].value,
    // interestedCity: [interestingCityOptions[0].value],
    // interestedFloorPlan: [floorPlanOptions[0].value],
    // availableTime: availableTimeOptions[0].value,
    // consultantTime: consultantTimeOptions[0].value,
    // whereDoYouKnowUsFrom: 'Facebook',
    agree: false,
  };
};

const Heading: React.FC = ({ children }) => {
  return <p className={classNames('py-3', 'text-center text-3xl font-medium')}>{children}</p>;
};

export const FormHeading = Heading;

const SectionTitle: React.FC = ({ children }) => {
  return <p className={'py-2 text-gold border-b border-solid border-gold'}>{children}</p>;
};

export const FormSectionTitle = SectionTitle;

export const FieldBlock: React.FC = ({ children }) => {
  return <div className={classNames('sm:flex-1')}>{children}</div>;
};

export const FieldRow: React.FC = ({ children }) => {
  return <div className={classNames('space-y-2', 'sm:space-y-0', 'sm:flex', 'sm:space-x-8')}>{children}</div>;
};

export const Form = React.forwardRef<HTMLFormElement, ElementPropsWithoutRef<'form'>>(({ children, ...props }, ref) => {
  return (
    <form className={StyleModule['form']} {...props} ref={ref}>
      {children}
    </form>
  );
});

export const SubmitButton: React.FC<
  ElementPropsWithoutRef<'button'> & {
    isLoading?: boolean;
  }
> = ({ isLoading, className, ...props }) => {
  return (
    <Button
      type={'submit'}
      mx={'auto'}
      display={'block'}
      height={'auto'}
      className={classNames(
        className,
        'mx-auto',
        'py-3 px-12',
        'disabled:text-gray500 text-white',
        'border border-solid disabled:border-gray500 border-white'
      )}
      isLoading={isLoading}
      {...props}
    >
      送出資料
    </Button>
  );
};

export const PersonalConsultantFormV2 = () => {
  const { value: lectureOptions = [] } = useAsync(async () => {
    const { data } = await FunwooAPI.systemApi.findAllStaticOptionsByCategory({
      category: StaticOptionCategory.CONFERENCE_LOCATION,
    });

    return createLectureOption(data);
  }, []);

  const router = useRouter();

  const methods = useForm<SendPersonalConsultationV2FormValues>({
    mode: 'all',
    defaultValues: getPersonalConsultantFormV2InitialValues(),
    // defaultValues: {
    //   lineId: '',
    //   comment: '',
    //   name: '廖季威',
    //   email: 'wei@funwoo.com.tw',
    //   industry: '製造業',
    //   jobLevel: '企業決策者（副總經理以上）',
    //   whereDoYouKnowUsFrom: '親友推薦',
    //   desireLecture: 'taichung_20250815_1000_1130',
    //   interestedCity: ['Seattle'],
    //   budget: '300w',
    //   dDay: '3個月內',
    //   availableTime: '平日下午 14:00-16:00',
    //   budgetCurrency: 'USD',
    //   phone: '0900000000',
    //   phoneCountryCode: 886,
    // },
  });
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = methods;

  const { isOpen, onClose, onOpen } = useDisclosure();

  const { executeRecaptcha } = useReCaptcha();

  const onSubmit = useCallback(
    handleSubmit(async ({ phone, phoneCountryCode, agree: _, ...formValues }) => {
      const { utm_source, utm_campaign, utm_content, utm_medium } = (router.query ?? {}) as UTM;

      const token = await executeRecaptcha('personal_consultation_v2');

      await FunwooAPI.mailApi.sendEmail(
        {
          type: 'PERSONAL_CONSULTATION_V2',
          ...formValues,
          phone: `${phoneCountryCode}-${phone}`,
          lineId: '',
          jobLevel: '',
          availableTime: '皆可',
          utm: {
            utm_source,
            utm_campaign,
            utm_content,
            utm_medium,
            userAgent: window.navigator.userAgent,
            ...trackLead(),
          },
        },
        {
          headers: {
            Recaptcha: token,
          },
        }
      );
      reset();
      onOpen();
    }, console.warn),
    [handleSubmit]
  );

  return (
    // @ts-ignore
    <FormProvider {...methods}>
      <GreetingPopup isOpen={isOpen} onClose={onClose} />
      <div className={clsx('bg-black', 'md:mx-auto', 'px-4 sm:px-16 sm:pt-4 pb-8', 'text-white')}>
        <Heading>報名表單</Heading>
        <Form onSubmit={onSubmit} className={StyleModule['form']}>
          <HintWording />
          <div className={classNames('pt-2', 'space-y-2')}>
            <div className={classNames('space-y-2')}>
              <SectionTitle>聯絡方式</SectionTitle>
              <FieldRow>
                <FieldBlock>
                  <NameField darkMode labelRight={'（ 請填寫出席者的中文全名，採實名制預約 ）'} />
                </FieldBlock>
              </FieldRow>
              <FieldRow>
                <FieldBlock>
                  <PhoneField darkMode />
                </FieldBlock>
                <FieldBlock>
                  <EmailField darkMode required />
                </FieldBlock>
              </FieldRow>
              <FieldRow>
                <FieldBlock>
                  <Label darkMode>產業</Label>
                  <PopupSelect
                    name={'industry'}
                    control={control}
                    rules={{ required: { value: true, message: '此欄位為必填' } }}
                    options={industryOptions}
                    isMultiple={false}
                    label={'選擇產業'}
                  />
                </FieldBlock>
                <FieldBlock>
                  <WhereDoYouKnowUsFrom darkMode popupMode />
                </FieldBlock>
              </FieldRow>
            </div>
            <div className={classNames('space-y-2')}>
              <SectionTitle>置產需求</SectionTitle>
              <FieldRow>
                <FieldBlock>
                  <Label darkMode>預約專人諮詢 / 參加說明會，選擇場次</Label>
                  <PopupSelect
                    name={'desireLecture'}
                    control={control}
                    rules={{ required: { value: true, message: '此欄位為必填' } }}
                    options={lectureOptions}
                    label={'預約專人諮詢 / 參加說明會，選擇場次'}
                    confirmWording={'確認'}
                  />
                  <HookFormError errors={errors} name={'desireLecture'} />
                </FieldBlock>
                <FieldBlock>
                  <Label darkMode>有興趣的美國城市</Label>
                  <PopupSelect
                    label={'置產城市'}
                    confirmWording={'選擇城市'}
                    control={control}
                    name={'interestedCity'}
                    rules={{ validate: multipleValueRequired }}
                    options={interestingCityOptions}
                    isMultiple
                  />
                  <HookFormError errors={errors} name={'interestedCity'} />
                </FieldBlock>
              </FieldRow>
              <FieldRow>
                <FieldBlock>
                  <Label darkMode required>
                    預算（美金）
                  </Label>
                  <FunwooRadio
                    control={control}
                    name={'budget'}
                    rules={{ required: { value: true, message: '此欄位為必填' } }}
                    options={budgetOptions}
                    colorScheme={'white'}
                  />
                  <HookFormError errors={errors} name={'budget'} />
                </FieldBlock>
                <FieldBlock>
                  <Label darkMode required>
                    計劃購買時程
                  </Label>
                  <FunwooRadio
                    control={control}
                    name={'dDay'}
                    rules={{ required: { value: true, message: '此欄位為必填' } }}
                    options={dDayOptions}
                    colorScheme={'white'}
                  />
                  <HookFormError errors={errors} name={'dDay'} />
                </FieldBlock>
              </FieldRow>
              <FieldRow>
                <FieldBlock>
                  <Label darkMode required={false}>
                    置產需求或問題
                  </Label>
                  <Input darkMode {...register('comment')} />
                </FieldBlock>
                {/*<FieldBlock>*/}
                {/*  <Label darkMode>方便聯繫的時段</Label>*/}
                {/*  <PopupSelect*/}
                {/*    label={'方便聯繫的時段'}*/}
                {/*    confirmWording={'選擇時段'}*/}
                {/*    control={control}*/}
                {/*    name={'availableTime'}*/}
                {/*    rules={{ required: { value: true, message: '此欄位為必填' } }}*/}
                {/*    options={availableTimeOptions}*/}
                {/*  />*/}
                {/*  <HookFormError errors={errors} name={'consultantTime'} />*/}
                {/*</FieldBlock>*/}
              </FieldRow>
            </div>
            <div className={classNames('sm:pt-4')}>
              <Agreement darkMode />
              <div className={'py-3'}>
                <SubmitButton type={'submit'} isLoading={isSubmitting}>
                  送出資料
                </SubmitButton>
              </div>
            </div>
          </div>
        </Form>
      </div>
    </FormProvider>
  );
};
